import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import "./YuxelGlobalFog.css";

/**
 * One persistent Fog canvas for the ENTIRE site, including route transitions.
 * Palette/interaction/scale reflect the supplied Vanta.js Fog reference.
 * The user-requested original Vanta Fog settings are applied without visual dilution.
 * The CSS fog fallback remains visible if WebGL is unavailable.
 */
export default function YuxelGlobalFog() {
  const host = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion || !host.current || typeof window === "undefined") return undefined;
    let disposed = false;
    let effect = null;
    const target = host.current;
    const supportsGL = () => {
      try {
        const probe = document.createElement("canvas");
        const gl = probe.getContext("webgl", { powerPreference: "low-power" }) || probe.getContext("experimental-webgl");
        if (!gl) return false;
        gl.getExtension("WEBGL_lose_context")?.loseContext();
        
        return true;
      } catch (_) { return false; }
    };
    if (!supportsGL()) return undefined;

    Promise.all([import("three"), import("vanta/dist/vanta.fog.min")])
      .then(([threeLib, fogLib]) => {
        if (disposed || !target.isConnected) return;
        const THREE = threeLib.default || threeLib;
        const Fog = fogLib.default || fogLib;
        effect = Fog({
          el: target, THREE,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200, minWidth: 200,
          scale: 2, scaleMobile: 4,
          backgroundAlpha: 1,
          // Exact original color integers from the Vanta Fog reference.
          // Do not convert to oxblood gradients or additional opaque overlays.
          baseColor: 590852,
          highlightColor: 2236441,
          lowlightColor: 131586,
          midtoneColor: 4673397,
          blurFactor: 0.6,
          speed: 1,
          zoom: 1,
        });
        target.dataset.fog = "webgl";
      })
      .catch(() => { if (target.isConnected) target.dataset.fog = "fallback"; });

    return () => {
      disposed = true;
      try { effect?.destroy?.(); } catch (_) { /* safe cleanup */ }
      delete target.dataset.fog;
    };
  }, [reduceMotion]);

  return (
    <div className="yx-global-fog" aria-hidden="true">
      <div className="yx-global-fog-fallback" />
      <div className="yx-global-fog-vanta" ref={host} />
      <div className="yx-global-fog-shade" />
    </div>
  );
}
